export const COACH_LEADER_LONGITUDINAL_EVENTS = {
  rendered: "coach_leader_longitudinal_rendered",
  sourceOpened: "coach_leader_longitudinal_source_opened",
  questionAdded: "coach_leader_longitudinal_question_added",
  askedVeera: "coach_leader_longitudinal_asked_veera",
} as const;

export type CoachLeaderLongitudinalAnalyticsAction = keyof typeof COACH_LEADER_LONGITUDINAL_EVENTS;
export type CoachLeaderLongitudinalAnalyticsState =
  | "loading"
  | "revoked"
  | "unavailable"
  | "insufficient_history"
  | "historical_signal"
  | "emerging_finding"
  | "evidence_backed_finding";

export type CoachLeaderLongitudinalAnalyticsInput = Readonly<{
  action: CoachLeaderLongitudinalAnalyticsAction;
  state: CoachLeaderLongitudinalAnalyticsState;
  aggregateSource?: "bi_v2" | "compatibility_only_v1" | "none";
  confidence?: "low" | "medium" | "high";
  exactSourceCoverage?: "none" | "some" | "threshold_met";
  authorizedSourceReadiness?: "ready" | "partial" | "unavailable";
  supportingEpisodeCount?: number;
  challengingEpisodeCount?: number;
  contradictionPresent?: boolean;
  contradictionCount?: number;
  contradictionContext?: "same_theme" | "cross_context" | "unknown";
  graphEvidenceCount?: number | null;
  routeSurface?: "coach_leader_detail";
  role: "coach";
}>;

export type CoachLeaderLongitudinalAnalyticsProperties = Readonly<{
  state: CoachLeaderLongitudinalAnalyticsState;
  aggregate_source?: "bi_v2" | "compatibility_only_v1" | "none";
  confidence?: "low" | "medium" | "high";
  exact_source_coverage?: "none" | "some" | "threshold_met";
  authorized_source_readiness?: "ready" | "partial" | "unavailable";
  supporting_episode_count?: number;
  challenging_episode_count?: number;
  contradiction_present?: boolean;
  contradiction_count?: number;
  contradiction_context?: "same_theme" | "cross_context" | "unknown";
  graph_evidence_count_bucket?: "unknown" | "0" | "1" | "2-5" | "6+";
  route_surface?: "coach_leader_detail";
  role: "coach";
}>;

const INPUT_KEYS = new Set<keyof CoachLeaderLongitudinalAnalyticsInput>([
  "action",
  "state",
  "aggregateSource",
  "confidence",
  "exactSourceCoverage",
  "authorizedSourceReadiness",
  "supportingEpisodeCount",
  "challengingEpisodeCount",
  "contradictionPresent",
  "contradictionCount",
  "contradictionContext",
  "graphEvidenceCount",
  "routeSurface",
  "role",
]);

const ACTIONS = new Set(Object.keys(COACH_LEADER_LONGITUDINAL_EVENTS));
const STATES = new Set<CoachLeaderLongitudinalAnalyticsState>([
  "loading",
  "revoked",
  "unavailable",
  "insufficient_history",
  "historical_signal",
  "emerging_finding",
  "evidence_backed_finding",
]);
const AGGREGATE_SOURCES = new Set(["bi_v2", "compatibility_only_v1", "none"]);
const CONFIDENCE_VALUES = new Set(["low", "medium", "high"]);
const EXACT_SOURCE_COVERAGE_VALUES = new Set(["none", "some", "threshold_met"]);
const AUTHORIZED_SOURCE_READINESS_VALUES = new Set(["ready", "partial", "unavailable"]);
const CONTRADICTION_CONTEXT_VALUES = new Set(["same_theme", "cross_context", "unknown"]);

function assertOptionalEnum(
  value: unknown,
  values: ReadonlySet<string>,
  label: string,
): asserts value is string | undefined {
  if (value !== undefined && (typeof value !== "string" || !values.has(value))) {
    throw new TypeError(`Unsupported ${label}`);
  }
}

function boundedCount(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.floor(value));
}

function graphEvidenceBucket(value: number | null): "unknown" | "0" | "1" | "2-5" | "6+" {
  if (value === null || !Number.isFinite(value)) return "unknown";
  const count = boundedCount(value);
  if (count === 0) return "0";
  if (count === 1) return "1";
  if (count <= 5) return "2-5";
  return "6+";
}

export function buildCoachLeaderLongitudinalAnalytics(
  input: CoachLeaderLongitudinalAnalyticsInput,
): CoachLeaderLongitudinalAnalyticsProperties {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new TypeError("Longitudinal analytics input must be an object");
  }
  for (const key of Object.keys(input)) {
    if (!INPUT_KEYS.has(key as keyof CoachLeaderLongitudinalAnalyticsInput)) {
      throw new TypeError(`Unsupported analytics key: ${key}`);
    }
  }
  if (!ACTIONS.has(input.action)) throw new TypeError("Unsupported analytics action");
  if (!STATES.has(input.state)) throw new TypeError("Unsupported analytics state");
  if (input.role !== "coach") throw new TypeError("Unsupported analytics role");
  assertOptionalEnum(input.aggregateSource, AGGREGATE_SOURCES, "aggregate source");
  assertOptionalEnum(input.confidence, CONFIDENCE_VALUES, "confidence");
  assertOptionalEnum(input.exactSourceCoverage, EXACT_SOURCE_COVERAGE_VALUES, "exact source coverage");
  assertOptionalEnum(input.authorizedSourceReadiness, AUTHORIZED_SOURCE_READINESS_VALUES, "authorized source readiness");
  assertOptionalEnum(input.contradictionContext, CONTRADICTION_CONTEXT_VALUES, "contradiction context");
  if (input.routeSurface !== undefined && input.routeSurface !== "coach_leader_detail") {
    throw new TypeError("Unsupported route surface");
  }
  if (input.contradictionPresent !== undefined && typeof input.contradictionPresent !== "boolean") {
    throw new TypeError("Unsupported contradiction presence");
  }

  return Object.freeze({
    state: input.state,
    ...(input.aggregateSource === undefined ? {} : { aggregate_source: input.aggregateSource }),
    ...(input.confidence === undefined ? {} : { confidence: input.confidence }),
    ...(input.exactSourceCoverage === undefined ? {} : { exact_source_coverage: input.exactSourceCoverage }),
    ...(input.authorizedSourceReadiness === undefined ? {} : { authorized_source_readiness: input.authorizedSourceReadiness }),
    ...(input.supportingEpisodeCount === undefined ? {} : { supporting_episode_count: boundedCount(input.supportingEpisodeCount) }),
    ...(input.challengingEpisodeCount === undefined ? {} : { challenging_episode_count: boundedCount(input.challengingEpisodeCount) }),
    ...(input.contradictionPresent === undefined ? {} : { contradiction_present: input.contradictionPresent }),
    ...(input.contradictionCount === undefined ? {} : { contradiction_count: boundedCount(input.contradictionCount) }),
    ...(input.contradictionContext === undefined ? {} : { contradiction_context: input.contradictionContext }),
    ...(input.graphEvidenceCount === undefined ? {} : { graph_evidence_count_bucket: graphEvidenceBucket(input.graphEvidenceCount) }),
    ...(input.routeSurface === undefined ? {} : { route_surface: input.routeSurface }),
    role: input.role,
  });
}
